import { Autocomplete, Button, Grid, Stack, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material"
import { FormEventHandler, useEffect, useState } from "react"
import ExcelJS from "exceljs";
import Attachment from "../../components/attachment/Attachment";
import { getCurrentDateTextIST } from "../../utilities/utils";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";

const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB

interface Vendor {
    vendorCode: string,
    companyName: string
}

interface PurchaseOrderRecord {
    expectedQty: number,
    skuCode: string,
    name: string
}

interface GRNRecord {
    skuCode: string;
    name: string;
    expectedQty: number;
    receivedQty: number;
    damaged: number;
    expiryDate: string;
}

const codeTypes = ['PO Code', 'Contract Code']

const grnFields = [
    { name: "SKU Code", key: "skuCode" },
    { name: "SKU Name", key: "name" },
    { name: "Expected Qty", key: "expectedQty" },
    { name: "Received Qty", key: "receivedQty" },
    { name: "Damaged", key: "damaged" },
    { name: "Expiry Date", key: "expiryDate" }
]

export default function NewInvoice() {
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [vendor, setVendor] = useState<Vendor | null>(null);
    const [codeType, setCodeType] = useState<(typeof codeTypes)[number]>('PO Code');
    const [code, setCode] = useState<string>('');
    const [grnAttachment, setGRNAttachment] = useState<File | null>(null);
    const [grnRecords, setGRNRecords] = useState<GRNRecord[]>([]);
    const [invoiceDate, setInvoiceDate] = useState<Dayjs | null>(null);
    const [invoiceAttachment, setInvoiceAttachment] = useState<File | null>(null);
    const [submit, setSubmit] = useState(false);
    // const [isSupportingRequired, setSupportingRequired] = useState(false);
    // const [supportingAttachment, setSupportingAttachment] = useState<File | null>(null);

    // ComponentDidMount
    useEffect(() => {
        fetch(`${process.env.REACT_APP_SERVER_URL}/vendor/all`)
            .then((response) => response.json())
            .then((res) => {
                console.log(res.data.vendors);
                setVendors(res.data.vendors);
            })
            .catch((error) => {
                console.error("API error:", error);
                // Handle the error
            });

        setInvoiceDate(dayjs(new Date()));

        return () => {
            // This code will run when the component is unmounted
            // You can perform any cleanup tasks here, such as unsubscribing from subscriptions
            console.log("Component unmounted");
        };
    }, []);

    const addNewInvoice: FormEventHandler = async (e) => {
        e.preventDefault();
        setSubmit(true);

        // Validation
        if (!vendor || !code || !invoiceDate || !grnAttachment || !invoiceAttachment || grnRecords.length === 0) {
            alert("Please fill all required fields");
            return;
        }

        try {
            const invoiceFormData = new FormData();
            invoiceFormData.append('poCode', code);
            invoiceFormData.append('invoiceDate', invoiceDate.toISOString());
            console.log(grnRecords)
            invoiceFormData.append('grnRecords', JSON.stringify(grnRecords.map(record => ({
                skuCode: record.skuCode,
                receivedQty: record.receivedQty,
                damaged: record.damaged,
                ...(record.expiryDate && { expiryDate: new Date(record.expiryDate).toISOString().slice(0, 10) })
            }))));

            const invoiceResponse = await fetch(`${process.env.REACT_APP_SERVER_URL}/invoice/new`, {
                method: 'POST',
                body: invoiceFormData
            });

            if (!invoiceResponse.ok) {
                throw new Error('Failed to create invoice');
            }
            const invoiceResponseJSON = await invoiceResponse.json();
            const totalChunks = Math.ceil(invoiceAttachment.size / CHUNK_SIZE);
            // 1. Initialize attachment upload for invoice
            const initFormData = new FormData();
            initFormData.append('name', invoiceAttachment.name);
            initFormData.append('mimeType', invoiceAttachment.type);
            initFormData.append('totalSizeInBytes', invoiceAttachment.size.toString());
            initFormData.append('totalChunks', totalChunks.toString());
            initFormData.append('attachmentType', 'invoiceAttachment');
            initFormData.append('entityName', 'Invoice');
            initFormData.append('entityId', invoiceResponseJSON.data.invoice.id);

            const invoiceInitResponse = await fetch(`${process.env.REACT_APP_SERVER_URL}/attachment/init`, {
                method: 'POST',
                body: initFormData
            });

            if (!invoiceInitResponse.ok) {
                throw new Error('Failed to initialize invoice attachment upload');
            }

            const invoiceInitData = await invoiceInitResponse.json();
            const invoiceAttachmentId = invoiceInitData.data.attachmentId;

            // Upload chunks in parallel
            const uploadPromises = [];
            for (let i = 0; i < totalChunks; i++) {
                const chunk = invoiceAttachment.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
                const chunkFormData = new FormData();
                chunkFormData.append('attachmentId', invoiceAttachmentId);
                chunkFormData.append('chunkIndex', i.toString());
                chunkFormData.append('file', chunk);

                const promise = fetch(`${process.env.REACT_APP_SERVER_URL}/attachment/uploadChunk`, {
                    method: 'POST',
                    body: chunkFormData,
                }).then(response => {
                    if (!response.ok) {
                        throw new Error(`Chunk ${i} failed to upload`);
                    }
                    return response.json();
                });

                uploadPromises.push(promise);
            }

            await Promise.all(uploadPromises);

            // Handle supporting document if required
            // if (isSupportingRequired && supportingAttachment) {
            //     // Similar FormData process for supporting document
            //     // ... (implement if needed)
            // }

            alert("Invoice created successfully!");

            // Reset form
            setVendor(null);
            setCode('');
            setGRNAttachment(null);
            setGRNRecords([]);
            setInvoiceAttachment(null);
            // setSupportingAttachment(null);
            setSubmit(false);

        } catch (error) {
            console.error("Error creating invoice:", error);
            alert(error instanceof Error ? error.message : "Failed to create invoice");
        }
    };

    const downloadGRNSheet = async () => {
        if (isPODetailsSubmitted()) {
            try {
                const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/purchase-order/${code}`)
                if (!res.ok) {
                    // Handle non-2xx responses
                    const errorData = await res.json().catch(() => ({ message: "Failed to fetch PO details" }));
                    throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
                }
                const data = await res.json();
                if (data?.data?.records) {
                    createAndDownloadGRNExcel(data.data.records);
                } else {
                    throw new Error("PO data structure is not as expected or records are missing.");
                }

            } catch (error) {
                console.error("API error:", error);
                // Handle the error
                alert("PO code is incorrect or not found");
            }
        } else {
            alert('Please enter PO Code or Contract Code');
        }
    }

    const createAndDownloadGRNExcel = async (records: PurchaseOrderRecord[]) => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("GRN Sheet");

            // Define columns based on grnFields
            worksheet.columns = grnFields.map(field => ({
                header: field.name,
                key: field.key,
                width: field.name.length < 15 ? 15 : field.name.length + 2 // Basic width adjustment
            }));

            // Add data rows
            records.forEach(record => {
                const rowData: { [key: string]: any } = {};
                grnFields.forEach(field => {
                    if (field.key === 'skuCode' || field.key === 'name' || field.key === 'expectedQty') {
                        // Fill data from the PurchaseOrderRecord
                        rowData[field.key] = record[field.key as keyof PurchaseOrderRecord];
                    } else {
                        // Leave other fields blank (like Received Qty, Damaged) for manual input
                        rowData[field.key] = '';
                    }
                });
                worksheet.addRow(rowData);
            });

            // Style header row (optional)
            worksheet.getRow(1).font = { bold: true };
            worksheet.getRow(1).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFCCCCCC' } // Light gray background
            };
            worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };


            // Generate filename
            const todayDate = getCurrentDateTextIST(); // Assuming format like YYYY-MM-DD
            const fileName = `GRN-${todayDate}`;

            // Write to buffer
            const buffer = await workbook.xlsx.writeBuffer();

            // Create a Blob
            const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

            // Trigger download using file-saver
            saveAs(blob, fileName);

        } catch (error) {
            console.error("Error creating or downloading Excel file:", error);
            alert("Failed to generate the GRN Excel sheet.");
        }
    }

    const saveAs = (blob: Blob, fileName: string) => {
        const url = URL.createObjectURL(new Blob([blob]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${fileName}.xlsx`;
        a.click();

        // Clean up by revoking the Blob URL
        URL.revokeObjectURL(url);
    };

    const updateGRNSheet = async (file: any) => {
        if (!file) {
            setGRNAttachment(null);
            setGRNRecords([]);
            return;
        }

        try {
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(file);
            const worksheet = workbook.getWorksheet("GRN Sheet");

            if (!worksheet) {
                throw new Error("Invalid GRN sheet format");
            }

            const records: GRNRecord[] = [];

            // Skip header row, start from row 2
            worksheet.eachRow((row, rowNumber) => {
                if (rowNumber === 1) return; // Skip header

                const record: GRNRecord = {
                    skuCode: row.getCell(1).value?.toString() || '',
                    name: row.getCell(2).value?.toString() || '',
                    expectedQty: Number(row.getCell(3).value) || 0,
                    receivedQty: Number(row.getCell(4).value) || 0,
                    damaged: Number(row.getCell(5).value) || 0,
                    expiryDate: row.getCell(6).value?.toString() || ''
                };

                // Validate required fields
                if (!record.skuCode || record.receivedQty === 0) {
                    throw new Error(`Invalid data in row ${rowNumber}`);
                }

                records.push(record);
            });

            setGRNRecords(records);
            setGRNAttachment(file);
        } catch (error) {
            console.error("Error parsing GRN sheet:", error);
            alert("Failed to parse GRN sheet. Please ensure it follows the correct format.");
            setGRNAttachment(null);
            setGRNRecords([]);
        }
    }

    const isPODetailsSubmitted = () => {
        if (vendor && code) {
            return true;
        }
        return false;
    }
    return (<Stack className="container" component="form" onSubmit={addNewInvoice}>
        <Grid
            container
            rowSpacing={1}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        >
            <Grid item xs={12}>
                <h2>Company</h2>
            </Grid>
            <Grid item xs={6}>
                <Autocomplete
                    disablePortal
                    id="vendor-code"
                    size="small"
                    options={vendors}
                    getOptionLabel={(option) => option?.vendorCode ?? ""}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Vendor Code"
                            required
                        />
                    )}
                    value={vendor}
                    onChange={(e, newValue) =>
                        setVendor(newValue)
                    }
                />
            </Grid>
            <Grid item xs={6}>
                <Autocomplete
                    disablePortal
                    id="company-name"
                    size="small"
                    options={vendors}
                    getOptionLabel={(option) => option?.companyName ?? ""}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Company Name"
                            required
                        />
                    )}
                    value={vendor}
                    onChange={(e, newValue) =>
                        setVendor(newValue)
                    }
                />
                {/* <TextField
                    id="company-name"
                    label="Company Name"
                    value={vendor?.companyName ?? ""}
                    InputProps={{
                        readOnly: true,
                    }}
                    fullWidth
                    size="small"
                /> */}
            </Grid>
            <Grid item xs={12}>
                <h2>Invoice Type</h2>
            </Grid>
            <Grid item xs={6}>
                <ToggleButtonGroup
                    color="primary"
                    value={codeType}
                    exclusive
                    fullWidth
                    onChange={(e, newCodeType) => newCodeType && setCodeType(newCodeType)}
                    size="small"
                >
                    {codeTypes.map((codeTypeValue) => (<ToggleButton key={codeTypeValue} value={codeTypeValue} sx={{ textTransform: 'none' }}>{codeTypeValue}</ToggleButton>))}
                </ToggleButtonGroup>
            </Grid>
            <Grid item xs={6}>
                <TextField
                    id="code"
                    label="Code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    fullWidth
                    size="small"
                />
            </Grid>
            <Grid item xs={6}>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="small"
                    onClick={downloadGRNSheet}
                >
                    Download GRN Sheet
                </Button>
            </Grid>
            <Grid item xs={6}>
                <Attachment
                    label="Upload GRN Sheet"
                    file={grnAttachment}
                    updateFile={(file) => updateGRNSheet(file)}
                    required={true}
                    submit={submit}
                />
            </Grid>
            <Grid item xs={12}>
                <h2>Invoice Details</h2>
            </Grid>
            <Grid item xs={6}>
                <DatePicker
                    label="Invoice Date"
                    value={invoiceDate}
                    onChange={(newValue) => setInvoiceDate(newValue)}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            size: 'small',
                            required: true,
                            error: submit && !invoiceDate,
                            helperText: submit && !invoiceDate ? "Invoice date is required" : ""
                        }
                    }}
                />
            </Grid>
            <Grid item xs={6}>
                <Attachment
                    label="Invoice Attachment"
                    file={invoiceAttachment}
                    updateFile={(file) => setInvoiceAttachment(file)}
                    required={true}
                    submit={submit}
                />
            </Grid>
            {/* <Grid item xs={6}>
                <FormControlLabel
                    control={
                        <Checkbox
                            color="primary"
                            onChange={(e) =>
                                setSupportingRequired(e.target.checked)
                            }
                        />
                    }
                    label="Is supporting required?"
                />
            </Grid>
            <Grid item xs={6}>
                {isSupportingRequired && <Attachment
                    label="Supporting Attachment"
                    file={supportingAttachment}
                    updateFile={(file) => setSupportingAttachment(file)}
                    required={true}
                    submit={submit}
                />}

            </Grid> */}
            <Grid item xs={12}>
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    size="small"
                >
                    Submit
                </Button>
            </Grid>
        </Grid>
    </Stack>)
}